import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import moment from "moment";
import ThriveCaseSelection from "../common/ThriveCaseSelection";
import ThriveModal from "../common/ThriveModal";
import ActivityBarChart from "./ActivityBarChart";
import IDEACoreCRM from "../IDEACoreCRM/IDEACoreCRM";
import ReminderEditModal from "./Reminders/ReminderEditModal";
import MetricTiles from "./TodayFeed/MetricTiles";
import PriorityFeed from "./TodayFeed/PriorityFeed";
import RecentCasesCard from "./TodayFeed/RecentCasesCard";
import {
  ActionPlanLaneItem,
  FeedFilter,
  FeedItem,
  FeedType,
  INACTIVE_DAYS,
  REFERRAL_MAX_DAYS,
  cardSx,
  referralDaysInPipeline,
} from "./TodayFeed/tokens";
import { axiosInstance, fetchInstance } from "../../utils/networking";
import { UserContext } from "../../context/UserContext";
import { ReminderType } from "../../interfaces";
import {
  filterActiveUsers,
  userStatsSummary,
} from "./ProgramStatistics/ProgramStatistics";

interface TodayData {
  counts: {
    reminders: number;
    referrals: number;
    actionPlans: number;
    inactiveClients: number;
    // Open in-scope cases with an interaction inside the INACTIVE_DAYS window.
    activeClients?: number;
    webIntakes: number;
    // In-pipeline referrals the server excluded from the lane (started
    // >= REFERRAL_MAX_DAYS ago). Optional so an older server reads as zero.
    olderReferrals?: number;
  };
  reminders: any[];
  referrals: any[];
  // Sorted by `nextDue` ascending server-side, and now includes upcoming
  // (future-dated) items, not just overdue/today.
  actionPlans: ActionPlanLaneItem[];
  inactiveClients: any[];
  webIntakes: { count: number; preview: any[] };
  // True when `inactiveClients` was truncated (server caps it at 50) while
  // `counts.inactiveClients` remains the true total.
  inactiveClientsCapped?: boolean;
}

// Reads an NDJSON stream (POST /cases/recent, POST /firms/massive_get) into an array.
const readNdjson = async (response: Response) => {
  if (!response.body) {
    throw new Error("ReadableStream not supported in this environment.");
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  const rows: any[] = [];
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";
    for (const line of lines) {
      if (!line.trim()) continue;
      try {
        rows.push(JSON.parse(line));
      } catch (err) {
        console.error("Failed to parse line:", line, err);
      }
    }
  }
  return rows;
};

const fetchRecentCases = (period: string) =>
  fetchInstance(`${process.env.REACT_APP_API_DOMAIN}/cases/recent`, {
    method: "POST",
    body: JSON.stringify({ period, case_status: "open", sortBy: "recent" }),
  }).then(readNdjson);

const EngagementsDashboard: React.FC = () => {
  const { user } = React.useContext(UserContext);

  const [today, setToday] = useState<TodayData | null>(null);
  // Inactive-lane sort. The today payload only carries the 50 MOST stale
  // clients, so "least stale first" cannot be a client-side re-sort of those
  // rows — it is a separate first page from GET /dashboard/inactive?order=asc,
  // fetched once on first flip and cached.
  const [inactiveSort, setInactiveSort] = useState<"desc" | "asc">("desc");
  const [inactiveAscRows, setInactiveAscRows] = useState<any[] | null>(null);
  const [reminders, setReminders] = useState<ReminderType[] | null>(null);
  const [filter, setFilter] = useState<FeedFilter>("all");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [userStats, setUserStats] = useState<any>({});
  const [recentCases, setRecentCases] = useState<any[]>([]);
  const [recentLoading, setRecentLoading] = useState(true);
  const [reminderFlag, setReminderFlag] = useState(false);

  // "All Cases" modal (Recent cases → View all) and the IDEACoreCRM case grid.
  const [allCaseCardOpen, setAllCaseCardOpen] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);
  const [crmFirms, setCrmFirms] = useState<any[]>([]);
  const [crmActivities, setCrmActivities] = useState<any[]>([]);
  const [crmOpenCases, setCrmOpenCases] = useState<any[]>([]);
  const [crmPeriod, setCrmPeriod] = useState("2w");
  const crmLoadedPeriodRef = React.useRef<string | null>(null);

  const refreshReminders = useCallback(() => setReminderFlag((f) => !f), []);

  useEffect(() => {
    axiosInstance
      .get(`/dashboard/today?inactiveDays=${INACTIVE_DAYS}`)
      .then((res) => setToday(res.data))
      .catch((err) => console.error("Failed to load today view:", err));

    axiosInstance
      .get(
        `/user/allUserStats?startDate=${dayjs()
          .dayOfYear(1)
          .format("YYYY-MM-DD")}&endDate=${""}`,
      )
      .then((res) => {
        if (user.role === "admin") {
          setUserStats(userStatsSummary(filterActiveUsers(res.data.userStats)));
        } else {
          setUserStats(
            res.data.userStats.find((item: any) => item.email === user.email),
          );
        }
      })
      .catch((err) => console.error("Failed to fetch user stats:", err));

    fetchRecentCases("2w")
      .then(setRecentCases)
      .catch((err) => console.error("Failed to fetch recent cases:", err))
      .finally(() => setRecentLoading(false));
  }, []);

  useEffect(() => {
    if (inactiveSort !== "asc" || inactiveAscRows !== null) return;
    axiosInstance
      .get(`/dashboard/inactive?inactiveDays=${INACTIVE_DAYS}&pageSize=50&order=asc`)
      .then((res) => setInactiveAscRows(res.data?.rows || []))
      .catch((err) => {
        console.error("Failed to load least-stale inactive clients:", err);
        setInactiveAscRows([]);
      });
  }, [inactiveSort, inactiveAscRows]);

  const toggleInactiveSort = useCallback(
    () => setInactiveSort((s) => (s === "desc" ? "asc" : "desc")),
    [],
  );

  useEffect(() => {
    axiosInstance
      .get(`/reminder?status=pending`)
      .then((res) => setReminders(res.data.reminders || []))
      .catch((err) => {
        console.error("Failed to fetch reminders:", err);
        setReminders([]);
      });
  }, [reminderFlag]);

  // The CRM modal loads its own (heavier) slice: cases + their activities + firms.
  const fetchCRMData = async (period: string) => {
    const cases = await fetchRecentCases(period);
    setCrmOpenCases(cases);
    if (!cases.length) return;

    const case_id = cases.map((c: any) => c._id).join(",") + ",";
    axiosInstance
      .post(`/activity/massive_get`, { case_id })
      .then((res) => setCrmActivities(res.data.activities))
      .catch((err) => console.error("Failed to fetch CRM activities:", err));

    const firms = await fetchInstance(
      `${process.env.REACT_APP_API_DOMAIN}/firms/massive_get`,
      {
        method: "POST",
        body: JSON.stringify({ ids: cases.map((c: any) => c.firmRecord._id) }),
      },
    ).then(readNdjson);
    setCrmFirms(firms);
  };

  useEffect(() => {
    if (cardOpen && crmLoadedPeriodRef.current !== crmPeriod) {
      crmLoadedPeriodRef.current = crmPeriod;
      fetchCRMData(crmPeriod);
    }
  }, [cardOpen]);

  const handleCrmPeriodChange = (event: any) => {
    const newPeriod = event.target.value;
    setCrmPeriod(newPeriod);
    setCrmOpenCases([]);
    setCrmActivities([]);
    setCrmFirms([]);
    crmLoadedPeriodRef.current = newPeriod;
    fetchCRMData(newPeriod);
  };

  // Handlers are memoized so PriorityFeed/MetricTiles (React.memo) only
  // re-render when the data they show actually changes.
  const completeReminder = useCallback(
    (id: string) => {
      setReminders((rs) => (rs || []).filter((r) => String(r._id) !== id));
      axiosInstance.post(`/reminder/${id}/complete`, {}).catch((err) => {
        console.error("Failed to complete reminder:", err);
        refreshReminders();
      });
    },
    [refreshReminders],
  );

  const deleteReminder = useCallback(
    (id: string) => {
      setReminders((rs) => (rs || []).filter((r) => String(r._id) !== id));
      axiosInstance.delete(`/reminder/${id}`).catch((err) => {
        console.error("Failed to delete reminder:", err);
        refreshReminders();
      });
    },
    [refreshReminders],
  );

  const snoozeReminder = useCallback(
    (id: string, days: number) => {
      axiosInstance
        .post(`/reminder/${id}/snooze`, { days })
        .then(refreshReminders)
        .catch((err) => console.error("Failed to snooze reminder:", err));
    },
    [refreshReminders],
  );

  // The typed text lives inside QuickAddBar (so keystrokes don't re-render
  // the whole dashboard); returns true on success so the bar clears itself.
  const submitQuickAdd = useCallback(
    async (title: string): Promise<boolean> => {
      setAdding(true);
      try {
        await axiosInstance.post(`/reminder`, {
          title,
          dueAt: dayjs()
            .add(1, "day")
            .hour(9)
            .minute(0)
            .second(0)
            .millisecond(0)
            .toISOString(),
          source: { kind: "manual" },
        });
        refreshReminders();
        return true;
      } catch (err) {
        console.error("Failed to add reminder:", err);
        return false;
      } finally {
        setAdding(false);
      }
    },
    [refreshReminders],
  );

  const selectFilter = useCallback(
    (type: FeedType) => setFilter((f) => (f === type ? "all" : type)),
    [],
  );
  const openAllCases = useCallback(() => setAllCaseCardOpen(true), []);
  const openCaseGrid = useCallback(() => setCardOpen(true), []);
  const closeEditing = useCallback(() => setEditing(null), []);

  const loading = today === null || reminders === null;

  // Reminders come from /reminder?status=pending (not the today lane) so that
  // complete/snooze/quick-add mutate the same list the feed renders.
  const sortedReminders = useMemo(
    () =>
      [...(reminders || [])].sort(
        (a, b) => moment(a.dueAt).valueOf() - moment(b.dueAt).valueOf(),
      ),
    [reminders],
  );

  // The server already sends the action-item lane in due-date order; re-sorting
  // here keeps that order explicit (and keeps a legacy/unsorted payload sane).
  // Undated items sink to the bottom rather than jumping to the top.
  const sortedActionPlans = useMemo(() => {
    const dueValue = (p: ActionPlanLaneItem) => {
      const due = p.nextDue ? moment(p.nextDue) : null;
      return due && due.isValid() ? due.valueOf() : Number.MAX_SAFE_INTEGER;
    };
    return [...(today?.actionPlans || [])].sort(
      (a, b) => dueValue(a) - dueValue(b),
    );
  }, [today]);

  const items: FeedItem[] = useMemo(() => {
    const rows: FeedItem[] = sortedReminders.map((r) => ({
      id: `reminder-${r._id}`,
      type: "reminders" as FeedType,
      raw: r,
    }));
    if (!today) return rows;
    return rows.concat(
      sortedActionPlans.map((p) => ({
        id: `plan-${p._id}`,
        type: "plans" as FeedType,
        raw: p,
      })),
      // Longest-in-pipeline first; PriorityFeed's sort toggle can flip it.
      [...today.referrals]
        .sort((a, b) => referralDaysInPipeline(b) - referralDaysInPipeline(a))
        .map((r) => ({
          id: `referral-${r._id}`,
          type: "referrals" as FeedType,
          raw: r,
        })),
      // desc = the today lane (50 most stale); asc = the cached least-stale
      // page, or the lane again until that page arrives.
      (inactiveSort === "asc" && inactiveAscRows
        ? inactiveAscRows
        : today.inactiveClients
      ).map((c) => ({
        id: `inactive-${c.case_id}`,
        type: "inactive" as FeedType,
        raw: c,
      })),
      (today.webIntakes?.preview || []).map((w) => ({
        id: `intake-${w.entry_id}`,
        type: "intakes" as FeedType,
        raw: w,
      })),
    );
  }, [sortedReminders, sortedActionPlans, today, inactiveSort, inactiveAscRows]);

  const counts: Record<FeedType, number> = useMemo(
    () => ({
      reminders: sortedReminders.length,
      referrals: today?.counts.referrals || 0,
      plans: today?.counts.actionPlans || 0,
      inactive: today?.counts.inactiveClients || 0,
      intakes: today?.counts.webIntakes || 0,
    }),
    [sortedReminders, today],
  );

  const subs: Record<FeedType, string> = useMemo(() => {
    const now = moment();
    const overdueReminders = sortedReminders.filter((r) =>
      moment(r.dueAt).isBefore(now),
    ).length;
    const followUpReferrals = (today?.referrals || []).filter(
      (r) => r.pipeline_status === "follow_up_needed",
    ).length;
    const overduePlans = (today?.actionPlans || []).filter(
      (p) => p.dueState === "overdue",
    ).length;
    return {
      reminders: `${overdueReminders} overdue`,
      // Both lanes are bounded server-side, so the subtitle names the band
      // rather than an open-ended "over N days".
      referrals: followUpReferrals
        ? `${followUpReferrals} need${
            followUpReferrals === 1 ? "s" : ""
          } follow-up`
        : `Started in the last ${REFERRAL_MAX_DAYS} days`,
      plans: `${overduePlans} overdue`,
      inactive: `interaction within ${INACTIVE_DAYS} days / none`,
      intakes: "awaiting assignment",
    };
  }, [sortedReminders, today]);

  // The Active / Inactive tile shows both halves of the 180-day split; the
  // tile's own count (and the feed it filters) stays the inactive half.
  const activeInactiveDisplay = useMemo(
    () => ({
      inactive: (
        <>
          <Box component="span" sx={{ color: "#2e7d32" }}>
            {today?.counts.activeClients ?? 0}
          </Box>
          <Box
            component="span"
            sx={{ color: "#b0b0b6", fontWeight: 600, margin: "0 6px" }}
          >
            /
          </Box>
          {today?.counts.inactiveClients || 0}
        </>
      ),
    }),
    [today],
  );

  const inactiveCapped =
    Boolean(today?.inactiveClientsCapped) ||
    (today?.counts.inactiveClients || 0) > (today?.inactiveClients?.length || 0);

  // Referrals the bounded lane dropped — the feed offers a way out to the
  // My Referrals page when the Referrals tile is selected.
  const olderCounts = useMemo(
    () => ({ referrals: today?.counts.olderReferrals || 0 }),
    [today],
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <MetricTiles
        counts={counts}
        subs={subs}
        displays={activeInactiveDisplay}
        filter={filter}
        onSelect={selectFilter}
        viewAllHrefs={{ inactive: "/dashboard/inactive" }}
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            lg: "minmax(0, 1.75fr) minmax(0, 1fr)",
          },
          gap: "20px",
          alignItems: "start",
        }}
      >
        <PriorityFeed
          items={items}
          counts={counts}
          filter={filter}
          onQuickAdd={submitQuickAdd}
          adding={adding}
          loading={loading}
          onComplete={completeReminder}
          onSnooze={snoozeReminder}
          onEdit={setEditing}
          onDelete={deleteReminder}
          inactiveCapped={inactiveCapped}
          olderCounts={olderCounts}
          inactiveSort={inactiveSort}
          onToggleInactiveSort={toggleInactiveSort}
          inactiveSortLoading={inactiveSort === "asc" && inactiveAscRows === null}
        />

        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <Box sx={cardSx}>
            <Box
              sx={{
                padding: "16px 18px 14px",
                borderBottom: "1px solid #efeff1",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 20,
                  borderRadius: "3px",
                  backgroundColor: "#0F95B3",
                }}
              />
              <Box>
                <Typography
                  sx={{ fontSize: "15px", fontWeight: 800, color: "#26262b" }}
                >
                  {user.role === "admin" ? "Program Stats" : "Your Stats"}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#8a8a90" }}>
                  This year · by type
                </Typography>
              </Box>
            </Box>
            <Box sx={{ height: 300, padding: "18px 18px 0" }}>
              <ActivityBarChart data={userStats?.userStats} />
            </Box>
          </Box>

          <RecentCasesCard
            cases={recentCases}
            loading={recentLoading}
            onViewAll={openAllCases}
            onOpenCaseGrid={openCaseGrid}
          />
        </Box>
      </Box>

      <ReminderEditModal
        open={Boolean(editing)}
        onClose={closeEditing}
        onSaved={refreshReminders}
        initial={
          editing
            ? {
                _id: String(editing._id),
                title: editing.title,
                note: editing.note,
                dueAt: editing.dueAt,
                firm_name: editing.firm_name,
              }
            : undefined
        }
      />

      <ThriveModal
        width={1280}
        open={cardOpen}
        setOpen={setCardOpen}
        contentComponent={
          <Box>
            <Box
              sx={{
                textAlign: "center",
                backgroundColor: "#9E1F63FF",
                padding: "3px",
                color: "white",
                borderRadius: "5px 5px 0 0",
                marginBottom: "20px",
              }}
            >
              <Typography sx={{ display: "inline-block" }} variant="h4">
                Current Cases
              </Typography>
              <Box
                sx={{ display: "inline", float: "right", cursor: "pointer" }}
                onClick={() => setCardOpen(false)}
              >
                <CloseIcon sx={{ fontSize: 40 }} />
              </Box>
            </Box>
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <FormControl sx={{ minWidth: 240, marginBottom: "20px" }}>
                <InputLabel id="crm-period-select-label">
                  Time Period
                </InputLabel>
                <Select
                  labelId="crm-period-select-label"
                  id="crm-period-select"
                  value={crmPeriod}
                  label="Time Period"
                  onChange={handleCrmPeriodChange}
                >
                  <MenuItem value={"1d"}>1 Day</MenuItem>
                  <MenuItem value={"1w"}>1 Week</MenuItem>
                  <MenuItem value={"2w"}>2 Weeks</MenuItem>
                  <MenuItem value={"1m"}>1 Month</MenuItem>
                  <MenuItem value={"2m"}>2 Months</MenuItem>
                  <MenuItem value={"all"}>All</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <IDEACoreCRM
              openCases={crmOpenCases}
              caseActivities={crmActivities}
              firms={crmFirms}
            />
          </Box>
        }
      />

      <ThriveModal
        height={700}
        open={allCaseCardOpen}
        contentComponent={
          <ThriveCaseSelection
            height={500}
            cases={[]}
            reloadCases={true}
            checkedCases={[]}
            setCheckedCases={() => {}}
            selectedCase={{}}
            setSelectedCase={() => {}}
            enableControl={true}
            enableSelection={false}
            enableMultiSelection={false}
            setOpen={setAllCaseCardOpen}
            title={"All Cases"}
            status_default="either"
            sortby_default="recent"
            period_default="all"
            handleNext={() => {}}
          />
        }
      />
    </Box>
  );
};

export default React.memo(EngagementsDashboard);
